import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
    language: string;
    setLanguage: (lang: string) => void;
    isRTL: boolean;
    toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
    undefined,
);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

interface LanguageProviderProps {
    children: React.ReactNode;
}

// Get initial language from localStorage synchronously
// Use 'i18nextLng' to match i18n's LanguageDetector
const getInitialLanguage = (): string => {
    if (typeof window !== 'undefined') {
        // Check i18n's localStorage key first
        const savedLanguage = localStorage.getItem('i18nextLng');
        if (savedLanguage === 'ar' || savedLanguage === 'en') {
            return savedLanguage;
        }
        // Fallback to 'language' key for backward compatibility
        const fallbackLanguage = localStorage.getItem('language');
        if (fallbackLanguage === 'ar' || fallbackLanguage === 'en') {
            // Migrate to i18nextLng
            localStorage.setItem('i18nextLng', fallbackLanguage);
            return fallbackLanguage;
        }
    }
    return 'en';
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
    children,
}) => {
    const { i18n } = useTranslation();
    // Initialize with language from localStorage synchronously
    const initialLang = getInitialLanguage();
    const [language, setLanguageState] = useState(initialLang);
    const isRTL = language === 'ar';

    const setLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setLanguageState(lang);
        // Use 'i18nextLng' to match i18n's LanguageDetector
        localStorage.setItem('i18nextLng', lang);
        // Also keep 'language' for backward compatibility
        localStorage.setItem('language', lang);

        // Update document direction and language
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;
    };

    const toggleLanguage = () => {
        const newLang = language === 'en' ? 'ar' : 'en';
        setLanguage(newLang);
    };

    useEffect(() => {
        // Ensure i18n and document are synced on mount
        // Get the saved language from localStorage (i18nextLng)
        const savedLanguage = getInitialLanguage();
        
        // If i18n has a different language than what's saved, update i18n
        if (i18n.language !== savedLanguage) {
            i18n.changeLanguage(savedLanguage);
        } else if (savedLanguage !== language) {
            // If i18n has the correct language but our state doesn't, sync state
            setLanguageState(savedLanguage);
        }
        
        // Ensure document attributes are set
        const currentLang = savedLanguage || i18n.language || 'en';
        document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = currentLang;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Listen for language changes
        const handleLanguageChange = (lng: string) => {
            setLanguageState(lng);
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);

    const value: LanguageContextType = {
        language,
        setLanguage,
        isRTL,
        toggleLanguage,
    };

    return (
        <LanguageContext.Provider value={value}>
            <div dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'rtl' : 'ltr'}>
                {children}
            </div>
        </LanguageContext.Provider>
    );
};
